const getWorkerKey = (workerId) => String(workerId);

const getTimeInMinutes = (time = '') => {
  const [hours, minutes] = time.split(':').map(Number);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
};

const getIntervalHours = (interval = {}) => {
  const fromMinutes = getTimeInMinutes(interval.from);
  const toMinutes = getTimeInMinutes(interval.to);

  if (fromMinutes === null || toMinutes === null) {
    return 0;
  }

  const durationMinutes =
    toMinutes >= fromMinutes
      ? toMinutes - fromMinutes
      : toMinutes + 24 * 60 - fromMinutes;

  return durationMinutes / 60;
};

const getWorkerWorkingHours = (workerIntervals = {}) =>
  Number(
    Object.values(workerIntervals)
      .reduce(
        (totalHours, interval) => totalHours + getIntervalHours(interval),
        0
      )
      .toFixed(2)
  );

const hasValidInterval = (interval) => interval?.from && interval?.to;

const hasWorkerIntervals = (workIntervals = {}, workerId) =>
  Object.values(workIntervals[getWorkerKey(workerId)] || {}).some(
    hasValidInterval
  );

const getScheduleWorkers = (schedule = {}) =>
  schedule.workers || schedule.performers || [];

export const getWorkersWithoutIntervals = (schedule = {}) => {
  const workIntervals = schedule.workIntervals || {};

  return getScheduleWorkers(schedule).filter(
    (worker) => !hasWorkerIntervals(workIntervals, worker.id)
  );
};

export const getWorkersWithIntervals = (schedule = {}) => {
  const workIntervals = schedule.workIntervals || {};

  return getScheduleWorkers(schedule).filter((worker) =>
    hasWorkerIntervals(workIntervals, worker.id)
  );
};

const getScheduleWorkersWithWorkingHours = (schedule) => {
  const workIntervals = schedule.workIntervals || {};

  return getScheduleWorkers(schedule).map((worker) => {
    const workerIntervals = workIntervals[getWorkerKey(worker.id)];

    if (!Object.values(workerIntervals || {}).some(hasValidInterval)) {
      return worker;
    }

    return {
      ...worker,
      workingHours: getWorkerWorkingHours(workerIntervals),
    };
  });
};

const normalizeScheduleWorkingHours = (schedule) => {
  const workers = getScheduleWorkersWithWorkingHours(schedule);

  return {
    ...schedule,
    workers,
    performers: workers,
  };
};

const getChildScheduleName = ({ sourceSchedule, values = {} }) => {
  const serviceSet = values.serviceSets?.[0];

  return (
    serviceSet?.name ||
    serviceSet?.workScheduleServiceName ||
    values.workScheduleServices?.workScheduleServiceName ||
    values.departmentName ||
    sourceSchedule?.departmentName ||
    'График по периоду'
  );
};

export const hasScheduleIntervals = (schedule) =>
  Object.values(schedule?.workIntervals || {}).some((intervalsByDate = {}) =>
    Object.values(intervalsByDate).some(hasValidInterval)
  );

export const getSchedulesForGroup = ({
  cachedChildSchedules,
  isChildScheduleList,
  scheduleGroups,
  selectedScheduleGroup,
}) => {
  if (!isChildScheduleList) {
    return scheduleGroups;
  }

  const cachedSchedulesForSelectedGroup = cachedChildSchedules.filter(
    (schedule) => String(schedule.parentId) === String(selectedScheduleGroup.id)
  );
  const editingDraftBySourceId = new Map(
    cachedSchedulesForSelectedGroup
      .filter(
        (schedule) =>
          schedule._draftMode === 'edit' &&
          schedule.sourceScheduleId !== undefined &&
          schedule.sourceScheduleId !== null
      )
      .map((schedule) => [String(schedule.sourceScheduleId), schedule])
  );
  const childSchedules = selectedScheduleGroup?.children || [];
  const persistedScheduleIds = new Set(
    childSchedules.map((schedule) => String(schedule.id))
  );
  const persistedSchedules = childSchedules.map(
    (schedule) => editingDraftBySourceId.get(String(schedule.id)) || schedule
  );
  const newDraftSchedules = cachedSchedulesForSelectedGroup.filter(
    (schedule) =>
      schedule._draftMode !== 'edit' ||
      !persistedScheduleIds.has(String(schedule.sourceScheduleId))
  );

  return [...persistedSchedules, ...newDraftSchedules];
};

export const createDraftSchedule = ({
  id,
  selectedScheduleGroup,
  sourceSchedule,
  values,
}) =>
  normalizeScheduleWorkingHours({
    ...values,
    id,
    parentId: selectedScheduleGroup?.id,
    departmentName: getChildScheduleName({ sourceSchedule, values }),
    startMonth: values.period?.startMonth,
    endMonth: values.period?.endMonth,
    workIntervals: values.workIntervals || {},
    isDraft: true,
  });

export const applyDraftIntervals = ({
  dates = [],
  interval,
  replaceDates = [],
  schedule,
  workerIds = [],
}) => {
  const workIntervals = { ...(schedule.workIntervals || {}) };
  const assignedWorkerKeySet = new Set(workerIds.map(getWorkerKey));
  const replaceDateSet = new Set(replaceDates);

  workerIds.forEach((workerId) => {
    const workerKey = getWorkerKey(workerId);
    const workerIntervals = {
      ...(workIntervals[workerKey] || {}),
    };

    replaceDateSet.forEach((date) => {
      delete workerIntervals[date];
    });

    dates.forEach((date) => {
      workerIntervals[date] = { ...interval };
    });

    workIntervals[workerKey] = workerIntervals;
  });

  const performers = (schedule.performers || []).map((performer) => {
    const workerKey = getWorkerKey(performer.id);

    if (!assignedWorkerKeySet.has(workerKey)) {
      return performer;
    }

    return {
      ...performer,
      workingHours: getWorkerWorkingHours(workIntervals[workerKey]),
    };
  });

  return {
    ...schedule,
    performers,
    workers: performers,
    workIntervals,
    isDraft: true,
  };
};
